
import React, { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { useChatMessages } from "./useChatMessages";
import ChatToggleButton from "./ChatToggleButton";
import ChatHeader from "./ChatHeader";
import ChatMessageList from "./ChatMessageList";
import ChatInput from "./ChatInput";

const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { messages, addMessage, clearMessages } = useChatMessages();

  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isMinimized]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setIsMinimized(false);
    }
  };

  const toggleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMinimized(!isMinimized);
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!message.trim() || isLoading) return;

    try {
      setIsLoading(true);
      addMessage({ role: "user", content: message });
      const userMessage = message;
      setMessage("");
      
      const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1iZHdoaGJjZ2JhZG5ta2VodGxpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQyMTE4MjgsImV4cCI6MjA1OTc4NzgyOH0.jLWlWEuN7gc5ofSQaP1Tjlq1gl2Nbz8RpyrrrIrMjMw';
      
      console.log('Sending request to Supabase Edge Function');
      const response = await fetch('https://mbdwhhbcgbadnmkehtli.supabase.co/functions/v1/openai-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${ANON_KEY}`,
          'apikey': ANON_KEY
        },
        body: JSON.stringify({ 
          prompt: userMessage,
          messages: messages.map(m => ({ role: m.role, content: m.content }))
        }),
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('API error response:', errorData);
        throw new Error(errorData.error || `API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.generatedText) {
        throw new Error("No text was generated");
      }

      addMessage({ 
        role: "assistant", 
        content: data.generatedText 
      });
    } catch (error) {
      console.error('Error in chat request:', error);
      toast({
        title: "Error",
        description: "Failed to get a response. Please try again.",
        variant: "destructive",
      });
      addMessage({ 
        role: "assistant", 
        content: "Sorry, I encountered an error. Please try again or check if the OpenAI integration is set up correctly." 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const resetChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearMessages();
  };

  return (
    <>
      <ChatToggleButton isOpen={isOpen} toggleChat={toggleChat} />

      <div
        className={cn(
          "fixed bottom-20 right-4 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-purple-200 dark:border-gray-700 transition-all duration-300 ease-in-out flex flex-col overflow-hidden z-50",
          isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none",
          isMinimized ? "h-14" : "w-80 sm:w-96 h-[500px] max-h-[80vh]"
        )}
      >
        <ChatHeader 
          isMinimized={isMinimized} 
          toggleMinimize={toggleMinimize} 
          resetChat={resetChat} 
        />

        <ChatMessageList 
          messages={messages} 
          isMinimized={isMinimized} 
        />

        {!isMinimized && (
          <ChatInput 
            message={message}
            setMessage={setMessage}
            handleSendMessage={handleSendMessage}
            isLoading={isLoading}
            handleKeyDown={handleKeyDown}
            inputRef={inputRef}
          />
        )}
      </div>
    </>
  );
};

export default ChatWidget;
